import { useEffect, useState } from 'react'
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import type { Me } from '@my-eng/shared'
import { TabBar } from './components/TabBar.js'
import { Today } from './screens/Today.js'
import { Placeholder } from './screens/Placeholder.js'
import { ApiError, login } from './lib/api.js'
import { backButton } from '@telegram-apps/sdk-react'
import styles from './App.module.css'

/** Screens that hide the tab bar: study is a focus mode, stack screens are modal. */
const FULLSCREEN_PATHS = ['/session', '/onboarding', '/placement', '/settings', '/admin']

export function App() {
  const [user, setUser] = useState<Me | null>(null)
  const [error, setError] = useState<string | null>(null)
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    login()
      .then((result) => setUser(result.user))
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Не удалось подключиться')
      })
  }, [])

  const isFullscreen = FULLSCREEN_PATHS.some((path) => location.pathname.startsWith(path))

  /**
   * Telegram's own BackButton drives navigation on stack screens — we do not
   * draw a custom one (CLAUDE.md > Screens & navigation).
   */
  useEffect(() => {
    if (!backButton.isMounted()) return

    if (isFullscreen) {
      backButton.show()
      return backButton.onClick(() => void navigate(-1))
    }
    backButton.hide()
    return undefined
  }, [isFullscreen, navigate])

  if (error) {
    return (
      <div className={styles.centered}>
        <Placeholder title="Нужен Telegram" text={error} />
      </div>
    )
  }

  if (!user) {
    return (
      <div className={styles.centered}>
        <p className={styles.loading}>Загружаем…</p>
      </div>
    )
  }

  return (
    <div className={styles.app}>
      <main className={styles.content}>
        <Routes>
          <Route path="/" element={<Today user={user} />} />
          <Route
            path="/words"
            element={
              <Placeholder
                title="Слова"
                text="Здесь будет твоя коллекция: поиск, фильтры, добавление слова и разбор карточки."
              />
            }
          />
          <Route
            path="/duels"
            element={
              <Placeholder
                title="Дуэли"
                text="Асинхронные тестовые дуэли: один и тот же набор слов для обоих, 10 вопросов, сутки на ответ."
              />
            }
          />
          <Route
            path="/stats"
            element={
              <Placeholder
                title="Статистика"
                text="Удержание, тепловая карта, прогноз нагрузки и список залипших слов."
              />
            }
          />
          <Route
            path="/session"
            element={
              <Placeholder
                title="Занятие"
                text="Плеер карточек появится, когда будет готов планировщик FSRS."
              />
            }
          />
          <Route
            path="/onboarding"
            element={
              <Placeholder
                title="Тест на уровень"
                text="Адаптивный тест из ~30 слов с псевдословами против угадывания."
              />
            }
          />
          <Route
            path="/settings"
            element={
              <Placeholder
                title="Настройки"
                text="Цель на день, время напоминаний, часовой пояс, видимость в рейтинге."
              />
            }
          />
          <Route path="*" element={<Placeholder title="Не найдено" text="Такого экрана нет." />} />
        </Routes>
      </main>

      {!isFullscreen && <TabBar />}
    </div>
  )
}
